import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  CardMedia,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import { useNavigate } from "react-router-dom";
import emptyCart from "../../assets/images/users/bag.png";
import { motion } from "framer-motion";
import CartQuantityManager from "./QuantityInput";
import DeleteIcon from "@mui/icons-material/Delete";
import { CartItem, removeCartItem, clearCart } from "../../app/services/CartService";
import { toast } from "react-toastify";

const CartPanel: React.FC = () => {
  const { cartStore,outletStore } = useStore();
  const { selectedOutlet } = outletStore;
  const navigate = useNavigate();

  const handleDelete = async (cart: CartItem) => {
    try {
      const res = await removeCartItem(cart.id);
      if (
        typeof res === "object" &&
        res !== null &&
        "status" in res &&
        "message" in res
      ) {
        if ((res as { status: string }).status === "success") {
          toast.success((res as { message: string }).message);
          cartStore.loadCartItems(cart.outlet_id);
        } else {
          toast.error((res as { message: string }).message);
        }
      } else {
        toast.error("Unexpected response from server");
      }
    } catch (error) {
      toast.error("Failed to delete Cart Item");
    }
  };

  const handleClearCart = async () => {
    if (typeof selectedOutlet?.id !== "number") {
      toast.error("No outlet selected");
      return;
    }

    try {
      const res = await clearCart(selectedOutlet.id);
      if (
        typeof res === "object" &&
        res !== null &&
        "status" in res &&
        "message" in res
      ) {
        if ((res as { status: string }).status === "success") {
          toast.success((res as { message: string }).message);
          const firstOutletId = cartStore.cart[0]?.outlet_id;
          if (firstOutletId) {
            cartStore.loadCartItems(firstOutletId);
          }
        } else {
          toast.error((res as { message: string }).message);
        }
      } else {
        toast.error("Unexpected response from server");
      }
    } catch (error) {
      toast.error("Failed to clear cart");
    }
  };

  return (
    <Box flex={1}>
      <Paper elevation={0} sx={{ p: 2, backgroundColor: "#fff" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700} gutterBottom>
            🛒 Cart
          </Typography>
          {cartStore.cart.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleClearCart}
              sx={{ textTransform: "none" }}
            >
              Clear All
            </Button>
          )}
        </Box>

        {cartStore.cart.length === 0 ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="300px"
              textAlign="center"
              sx={{ opacity: 0.7 }}
            >
              <img
                src={emptyCart}
                alt="empty-cart"
                width={100}
                style={{ marginBottom: 8 }}
              />
              <Typography variant="body1" fontWeight={500}>
                Your cart looks empty!
              </Typography>
              <Typography variant="body2">Add products to checkout.</Typography>
            </Box>
          </motion.div>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {cartStore.cart.map((item) => {
              // ✅ Defensive check: ensure product exists
              if (!item.product) {
                return (
                  <Typography
                    key={item.id}
                    color="error"
                    variant="body2"
                    sx={{ p: 1 }}
                  >
                    Invalid product data
                  </Typography>
                );
              }

              const price = item.custom_price
                ? parseFloat(item.custom_price)
                : parseFloat(item.product.base_price ?? "0");

              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <Box display="flex" alignItems="center" p={1}>
                    <CardMedia
                      component="img"
                      image={
                        item.product.images?.[0]?.path || "/placeholder.jpg"
                      }
                      alt={item.product.name ?? "Product"}
                      sx={{
                        width: 90,
                        height: 90,
                        objectFit: "cover",
                        borderRadius: 1,
                        mr: 1.5,
                      }}
                    />
                    <Box flex={1}>
                      <Typography fontWeight={600}>
                        {item.product.name ?? "Unnamed Product"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        ₹ {price}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {parseInt(item.product.base_quantity ?? "0")}{" "}
                        {item.product.base_unit ?? ""}
                      </Typography>

                      {item.base_quantity && (
                        <Typography variant="body2" color="text.secondary">
                          {item.base_quantity} {item.base_unit}
                        </Typography>
                      )}
                    </Box>

                    <Box textAlign="center" px={2}>
                      <Typography fontWeight={600}>
                        ₹ {item.quantity * price}
                        <DeleteIcon
                          sx={{ color: "red", ml: 1, cursor: "pointer" }}
                          onClick={() => handleDelete(item)}
                        />
                      </Typography>

                      <CartQuantityManager cartItem={item} />
                    </Box>
                  </Box>
                </motion.div>
              );
            })}

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="subtitle1"
              fontWeight={700}
              textAlign="right"
              mb={2}
            >
              Total: ₹ {Number(cartStore.totalAmount).toFixed(2)}
            </Typography>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Button
                fullWidth
                variant="contained"
                sx={{ backgroundColor: "#00703C", textTransform: "none" }}
                onClick={() => navigate("/checkout")}
              >
                Check Out →
              </Button>
            </motion.div>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default observer(CartPanel);
